import React from "react";
import { motion } from "framer-motion";
import { ButtonClose, ButtonPopup } from "../ui";
import { useNavigate } from "react-router-dom";

const EliminarCuenta: React.FC = () => {
  const navigate = useNavigate();

  const handleClose = () => {
    navigate(-2); // Navega dos pasos atrás en el historial
  };

  return (
    <div
      className="w-full h-screen flex items-center justify-center px-4 py-10 bg-cover bg-center relative"
      style={{ backgroundImage: "url('/img/politicas/fondoLinkservs.png')" }}
    >
      {/* Overlay */}
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" />

      <motion.div
        className="relative z-10 w-[210mm] max-w-full max-h-[95vh] rounded-2xl border border-white/20 shadow-2xl bg-black/30 backdrop-blur-xs overflow-hidden"
        initial={{ opacity: 0, y: 20, scale: 0.95 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        transition={{ duration: 0.5 }}
      >
        {/* Contenido scrollable */}
        <div className="p-8 overflow-y-auto max-h-[95vh] text-white">

          {/* Botón cerrar */}
          <div className="flex justify-end mb-6">
            <ButtonClose onClick={handleClose} />
          </div>

          {/* Título */}
          <h1 className="text-4xl font-extrabold text-center mb-4 text-white">
            Eliminación de Cuenta
          </h1>

          <p className="text-sm text-zinc-800 mb-8 text-center">
            <strong>LinkServs · CuadraNex</strong>
          </p>

          {/* Introducción */}
          <div className="space-y-6 leading-relaxed text-justify">

            <p>
              En <strong>LinkServs</strong>, gestionado por{" "}
              <strong>CuadraNex</strong>, respetamos tu derecho a solicitar la
              eliminación de tu cuenta y de los datos personales asociados a ella.
            </p>

            {/* Cómo solicitar la eliminación */}
            <section>
              <h2 className="text-xl font-semibold mt-6 mb-3 text-zinc-800">
                1. Cómo solicitar la eliminación de tu cuenta
              </h2>

              <p>
                Para solicitar la eliminación de tu cuenta de LinkServs, debes
                enviar una solicitud por correo electrónico a:
              </p>

              <div className="mt-4 flex justify-center">
                <a
                  href="mailto:cuadranex@cuadranex.com?subject=Solicitud%20de%20eliminación%20de%20cuenta%20LinkServs"
                  className="inline-flex items-center justify-center px-6 py-3 rounded-xl
                             bg-white/90 text-zinc-900 font-semibold shadow-lg
                             hover:bg-white hover:scale-[1.02]
                             transition-all duration-200"
                >
                  Solicitar eliminación por correo
                </a>
              </div>

              <p className="mt-4">
                En la solicitud deberás indicar la dirección de correo electrónico
                asociada a tu cuenta de LinkServs y expresar claramente que deseas
                eliminar tu cuenta.
              </p>
            </section>

            {/* Proceso */}
            <section>
              <h2 className="text-xl font-semibold mt-6 mb-3 text-zinc-800">
                2. Proceso de eliminación
              </h2>

              <p>
                Una vez recibida la solicitud, podremos realizar las comprobaciones
                necesarias para verificar que la petición procede del titular de
                la cuenta.
              </p>

              <p className="mt-3">
                Una vez verificada la solicitud, procederemos a eliminar la cuenta
                y los datos personales asociados que no sea necesario conservar por
                motivos legales o legítimos.
              </p>

              <p className="mt-3">
                Cuando el proceso haya finalizado, te informaremos mediante el
                correo electrónico utilizado para realizar la solicitud.
              </p>
            </section>

            {/* Datos que se eliminan */}
            <section>
              <h2 className="text-xl font-semibold mt-6 mb-3 text-zinc-800">
                3. Datos que se eliminan
              </h2>

              <p>
                La eliminación de la cuenta implica la eliminación de los datos
                personales asociados a ella que ya no sean necesarios para la
                prestación del servicio.
              </p>

              <ul className="list-disc ml-6 mt-3 space-y-1">
                <li>Datos identificativos asociados a la cuenta.</li>
                <li>Información de contacto asociada al usuario.</li>
                <li>Información del perfil de usuario.</li>
                <li>Datos de acceso y credenciales de la cuenta.</li>
                <li>Otros datos personales asociados directamente a la cuenta.</li>
              </ul>
            </section>

            {/* Datos que pueden conservarse */}
            <section>
              <h2 className="text-xl font-semibold mt-6 mb-3 text-zinc-800">
                4. Datos que pueden conservarse
              </h2>

              <p>
                Determinados datos podrán conservarse cuando exista una obligación
                legal que así lo requiera, por ejemplo, obligaciones fiscales,
                contables o relacionadas con la gestión de posibles reclamaciones
                o responsabilidades legales.
              </p>

              <p className="mt-3">
                Estos datos se conservarán únicamente durante el periodo necesario
                para cumplir con dichas obligaciones y, cuando corresponda,
                permanecerán bloqueados y no serán utilizados para otras
                finalidades.
              </p>
            </section>

            {/* Retención */}
            <section>
              <h2 className="text-xl font-semibold mt-6 mb-3 text-zinc-800">
                5. Periodo de conservación
              </h2>

              <p>
                Los datos que deban conservarse por obligación legal se mantendrán
                durante el periodo establecido por la normativa aplicable.
                Finalizado dicho periodo, serán eliminados o anonimizados cuando
                resulte técnicamente posible.
              </p>
            </section>

            {/* Contacto */}
            <section>
              <h2 className="text-xl font-semibold mt-6 mb-3 text-zinc-800">
                6. Contacto
              </h2>

              <p>
                Para cualquier consulta relacionada con la eliminación de tu cuenta
                o el tratamiento de tus datos personales, puedes contactar con
                nosotros en:
              </p>

              <ul className="list-disc ml-6 mt-3 space-y-1">
                <li>
                  <strong>Empresa:</strong> CuadraNex
                </li>
                <li>
                  <strong>Email:</strong>{" "}
                  <a
                    href="mailto:cuadranex@cuadranex.com"
                    className="underline hover:text-white"
                  >
                    cuadranex@cuadranex.com
                  </a>
                </li>
                <li>
                  <strong>Teléfono:</strong> 925 00 41 05
                </li>
                <li>
                  <strong>Sitio web:</strong> www.linkservs.com
                </li>
              </ul>
            </section>

            {/* Política de privacidad */}
            <section>
              <h2 className="text-xl font-semibold mt-6 mb-3 text-zinc-800">
                7. Información adicional
              </h2>

              <p>
                Para obtener información más detallada sobre el tratamiento,
                protección y conservación de tus datos personales, puedes consultar
                nuestra Política de Privacidad.
              </p>

              <div className="flex justify-center mt-4">
                <button
                  type="button"
                  onClick={() => navigate("/politicaPrivacidad")}
                  className="px-5 py-2.5 rounded-lg border border-white/30
                             bg-white/10 text-white font-semibold
                             hover:bg-white/20 transition-all duration-200"
                >
                  Ver Política de Privacidad
                </button>
              </div>
            </section>
          </div>

          {/* Botón inferior */}
          <div className="flex justify-center mt-10">
            <ButtonPopup onClick={handleClose}>
              Cerrar
            </ButtonPopup>
          </div>
        </div>
      </motion.div>
    </div>
  );
};

export default EliminarCuenta;